import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  defaultReportRange,
  REPORT_ADVANCED_SECTION_IDS,
  REPORT_LAYOUT_SURFACE,
  reportRangeAfterPick,
  type ReportRangeAdjustment,
  type StoredWidgetLayout,
} from "@heraion/shared";

import { WidgetConfigSheet } from "../Home/WidgetConfigSheet";
import { useWidgetLayout } from "../Home/useWidgetLayout";
import { PageSheet } from "../primitives/PageSheet";
import {
  ProgressReportContent,
  type ShareReportImage,
} from "./ProgressReportContent";
import { PROGRESS_WIDGET_COLUMN, progressWidgetLabel } from "./progressWidgetCatalog";
import type { ProgressDeps } from "./types";
import { usePeriodCaches } from "./usePeriodCaches";

export type { ShareReportImage } from "./ProgressReportContent";

type FormatDate = (iso: string, locale: string) => string;

// Periods whose rows stay in memory (usePeriodCaches).
const CACHED_PERIODS = 4;

// PG-64: the report generator, opened from Progress. It keeps what outlives
// one opening: the period, the sections arranged with the PG-57 engine on a
// layout that lives only while the app runs, and one results cache per
// period. What reads data (ProgressReportContent) mounts only while the sheet
// is open. There is no entitlement yet, so every athlete gets the base report.
export function ProgressReportSheet({
  deps,
  visible,
  onClose,
  formatDate,
  locale,
  onShareImage,
  onShareError,
  sheetInsets,
  title,
}: {
  deps: ProgressDeps;
  visible: boolean;
  onClose: () => void;
  formatDate: FormatDate;
  locale: string;
  /** Absent: no Share button. */
  onShareImage?: ShareReportImage;
  onShareError?: (message: string) => void;
  sheetInsets?: { top: number; bottom: number };
  /** The coach's sheet names the athlete. Absent: «Training report». */
  title?: string;
}) {
  const { theme } = deps;
  const { t } = useTranslation();

  const [range, setRange] = useState(() => defaultReportRange(Date.now()));
  const [adjusted, setAdjusted] = useState<ReportRangeAdjustment | null>(null);
  const onPick = useCallback(
    (end: "from" | "to", pickedMs: number) => {
      const next = reportRangeAfterPick(range, end, pickedMs, Date.now());
      setRange(next.range);
      setAdjusted(next.adjusted);
    },
    [range],
  );
  const resultsCache = usePeriodCaches(range, CACHED_PERIODS);
  const reportDeps = useMemo(
    () => ({
      ...deps,
      range,
      resultsRange: range,
      resultsCache,
      // The disk copy is Progress's newest rows, not this period.
      readStaleResults: undefined,
      persistResults: undefined,
      diskCache: undefined,
    }),
    [deps, range, resultsCache],
  );

  const [stored, setStored] = useState<StoredWidgetLayout | undefined>();
  const sections = useWidgetLayout(REPORT_LAYOUT_SURFACE, {
    stored,
    save: async (next) => setStored(next),
  });
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const premiumHint = t("mobile.progress.report.premiumHint", {
    defaultValue: "Premium options are not part of the base report.",
  });

  return (
    <PageSheet
      theme={theme}
      visible={visible}
      onRequestClose={onClose}
      title={
        title ?? t("mobile.progress.report.title", { defaultValue: "Training report" })
      }
      cancelLabel={t("mobile.common.close", { defaultValue: "Close" })}
      topInset={sheetInsets?.top}
      bottomInset={sheetInsets?.bottom}
    >
      <ProgressReportContent
        deps={reportDeps}
        order={sections.layout.order}
        adjusted={adjusted}
        onPick={onPick}
        onChooseSections={() => setSectionsOpen(true)}
        premiumHint={premiumHint}
        formatDate={formatDate}
        locale={locale}
        onShareImage={onShareImage}
        onShareError={onShareError}
      />
      <WidgetConfigSheet
        theme={theme}
        visible={sectionsOpen}
        onClose={() => setSectionsOpen(false)}
        title={t("mobile.progress.report.sectionsTitle", {
          defaultValue: "Report sections",
        })}
        // A layout held in memory cannot fail to save.
        saveErrorText=""
        surface={REPORT_LAYOUT_SURFACE}
        labelOf={(id) => progressWidgetLabel(t, id)}
        columnOf={(id) => PROGRESS_WIDGET_COLUMN[id]}
        layout={sections.layout}
        onMove={sections.move}
        onHide={sections.hide}
        onShow={sections.show}
        onReset={sections.reset}
        isDefault={sections.isDefault}
        saveFailed={false}
        topInset={sheetInsets?.top}
        bottomInset={sheetInsets?.bottom}
        locked={{
          ids: REPORT_ADVANCED_SECTION_IDS,
          title: t("mobile.progress.report.advancedSections", {
            defaultValue: "Advanced sections",
          }),
          hint: premiumHint,
        }}
      />
    </PageSheet>
  );
}
